import {
  GraphQLID,
  GraphQLNonNull,
  GraphQLInputObjectType,
  GraphQLString,
  GraphQLList,
  GraphQLBoolean
} from 'graphql'
import { Context } from '../shared'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import { ContrastGraphQLFieldConfig } from '../index'

export interface AttestationReportRequest {
  readonly vulnerabilityStatuses: string[]
  readonly vulnerabilitySeverities: string[]
  readonly vulnerabilityTypes: string[]
  readonly vulnerabilityTags: string[]
  readonly serverEnvironments: string[]
  readonly serverTags: string[]
  readonly complianceReports: string[]
  readonly showVulnerabilitiesDetails: boolean
  readonly showRouteObservations: boolean
}

export const attestationReportRequestType = new GraphQLInputObjectType({
  name: 'AttestationReportRequestInput',
  fields: () => ({
    vulnerabilityStatuses: {
      description: 'Vulnerability statuses for filtering',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    vulnerabilitySeverities: {
      description: 'Vulnerability severities for filtering',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    vulnerabilityTypes: {
      description: 'Vulnerability types for filtering',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    vulnerabilityTags: {
      description: 'Vulnerability tags for filtering',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    serverEnvironments: {
      description: 'Server environments for filtering',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    serverTags: {
      description: 'Server tags for filtering',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    complianceReports: {
      description: 'Compliance reports for filtering',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    showVulnerabilitiesDetails: {
      description:
        'flag to verify if Vulnerabilities Details should be included',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    showRouteObservations: {
      description: 'flag to verify if Route Observations should be included',
      type: new GraphQLNonNull(GraphQLBoolean)
    }
  })
})

export interface GenerateAttestationReportArgs {
  readonly input: {
    readonly organizationID: string
    readonly applicationID: string
    readonly request: AttestationReportRequest
  }
}

export const generateAttestationReportArgsType = new GraphQLInputObjectType({
  name: 'GenerateAttestationReportArgs',
  description:
    'Parameters to submit request to generate Attestation Report for a specific application',
  fields: () => ({
    organizationID: {
      description: 'The ID of the organization',
      type: new GraphQLNonNull(GraphQLID)
    },
    applicationID: {
      description: 'The ID of the application',
      type: new GraphQLNonNull(GraphQLID)
    },
    request: {
      description: 'Attestation Report Request',
      type: new GraphQLNonNull(attestationReportRequestType)
    }
  })
})

export const generateAttestationReportArgs = {
  input: {
    type: generateAttestationReportArgsType
  }
}

export const generateAttestationReport: ContrastGraphQLFieldConfig<
  undefined,
  Context,
  GenerateAttestationReportArgs,
  GenericMutationResponse
> = {
  description:
    'Submit request to generate Attestation Report for a specific application',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: generateAttestationReportArgs,
  resolve: async (_source, args, { connector }) => {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const {
      input: { organizationID, applicationID, request }
    } = args

    await connector.post(
      `/${organizationID}/applications/${applicationID}/attestation`,
      {
        ...request
      }
    )

    return { _placeholder: true }
  }
}
